import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { NotificationCenter } from '../components/NotificationCenter';
import {
  HouseSimple,
  SignOut,
  Lightning,
  ShieldCheck,
  Package,
  ChartLineUp,
  FileText,
  CaretDown,
  Briefcase,
  CurrencyCircleDollar,
  Database,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Small direct nav button ─────────────────────── */
const NavLink = ({ label, icon: Icon, iconColor = '', path, active, onClick, testId }) => (
  <button
    onClick={onClick}
    className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-sm transition-colors flex items-center gap-1.5 whitespace-nowrap ${
      active ? 'bg-white/15 text-white' : 'text-zinc-400 hover:text-white hover:bg-white/10'
    }`}
    data-testid={testId}
  >
    <Icon size={15} weight="fill" className={iconColor} />
    {label}
  </button>
);

/* ── Dropdown menu ────────────────────────────────── */
const NavDropdown = ({ label, icon: Icon, iconColor, items, isActive, navigate, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  if (!items.length) return null;

  const anyActive = items.some((i) => isActive(i.path));

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-sm flex items-center gap-1 transition-colors whitespace-nowrap ${
          anyActive || open ? 'bg-white/15 text-white' : 'text-zinc-400 hover:text-white hover:bg-white/10'
        }`}
        data-testid={testId}
      >
        <Icon size={15} weight="fill" className={iconColor} />
        {label}
        <CaretDown size={10} weight="bold" className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1.5 bg-zinc-800 border border-zinc-700 rounded-md shadow-2xl py-1 min-w-[180px] z-[60]">
          {items.map((item) => (
            <button
              key={item.path + item.label}
              onClick={() => { navigate(item.path); setOpen(false); }}
              className={`w-full text-left px-4 py-2 text-[11px] font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-white/10 text-white'
                  : 'text-zinc-300 hover:bg-white/5 hover:text-white'
              }`}
              data-testid={item.testId}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Main layout ──────────────────────────────────── */
export const AppLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const isAdmin = user?.role && ['super_admin', 'admin'].includes(user.role);

  /* — dropdown configs — */
  const projectItems = [
    { label: 'All Projects', path: '/dashboard', testId: 'nav-all-projects' },
    { label: 'BOQ / VO', path: '/rfq-generator', testId: 'nav-boq-vo' },
    { label: 'Execution', path: '/running-bill', testId: 'nav-execution' },
  ];

  const procurementItems = [
    { label: 'Materials', path: '/material-master', testId: 'nav-materials' },
    { label: 'Procurement', path: '/procurement', testId: 'nav-procurement' },
    { label: 'Vendors', path: '/vendors', testId: 'nav-vendors' },
  ];

  const financeItems = [
    { label: 'Billing / Invoices', path: '/running-bill', testId: 'nav-billing' },
    { label: 'CN / DN', path: '/adjustments', testId: 'nav-cn-dn' },
  ];

  const masterItems = [
    ...(isAdmin ? [
      { label: 'Companies', path: '/companies', testId: 'nav-companies' },
      { label: 'Trades', path: '/trades', testId: 'nav-trades' },
    ] : []),
    { label: 'Clients', path: '/clients', testId: 'nav-clients' },
    { label: 'Line Item Master', path: '/line-item-master', testId: 'nav-line-item-master' },
    { label: 'Master Studio', path: '/master-studio', testId: 'nav-master-studio' },
    { label: 'Item Suggestions', path: '/item-suggestions', testId: 'nav-item-suggestions' },
  ];

  const adminItems = isAdmin ? [
    { label: 'Users', path: '/user-management', testId: 'nav-users' },
    { label: 'Permissions', path: '/permissions', testId: 'nav-permissions' },
    { label: 'Audit', path: '/audit', testId: 'nav-audit' },
    { label: 'Health Check', path: '/system-health', testId: 'nav-health-check' },
  ] : [];

  return (
    <div className="min-h-screen bg-zinc-50">
      <nav className="bg-zinc-900 text-white border-b border-zinc-800 sticky top-0 z-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Left: logo + nav items */}
            <div className="flex items-center gap-1">
              <img
                src="/assets/sg-decor-logo.png"
                alt="SG Decor"
                className="h-8 cursor-pointer brightness-0 invert mr-3"
                onClick={() => navigate('/dashboard')}
                data-testid="app-logo"
              />

              <div className="hidden sm:flex items-center gap-0.5">
                {/* ── Top-level direct links ── */}
                <NavLink label="Dashboard" icon={HouseSimple} path="/dashboard" active={isActive('/dashboard')} onClick={() => navigate('/dashboard')} testId="nav-dashboard" />
                {isAdmin && (
                  <NavLink label="Command Center" icon={ChartLineUp} iconColor="text-emerald-400" path="/command-center" active={isActive('/command-center')} onClick={() => navigate('/command-center')} testId="nav-command-center" />
                )}
                <NavLink label="Action Center" icon={Lightning} iconColor="text-amber-400" path="/action-center" active={isActive('/action-center')} onClick={() => navigate('/action-center')} testId="nav-action-center" />
                <NavLink label="Reports" icon={FileText} iconColor="text-blue-400" path="/reports" active={isActive('/reports')} onClick={() => navigate('/reports')} testId="nav-reports" />

                {/* ── Divider ── */}
                <div className="w-px h-5 bg-zinc-700 mx-1" />

                {/* ── Dropdown groups ── */}
                <NavDropdown label="Projects" icon={Briefcase} iconColor="text-cyan-400" items={projectItems} isActive={isActive} navigate={navigate} testId="nav-dropdown-projects" />
                <NavDropdown label="Procurement" icon={Package} iconColor="text-orange-400" items={procurementItems} isActive={isActive} navigate={navigate} testId="nav-dropdown-procurement" />
                <NavDropdown label="Finance" icon={CurrencyCircleDollar} iconColor="text-green-400" items={financeItems} isActive={isActive} navigate={navigate} testId="nav-dropdown-finance" />
                <NavDropdown label="Masters" icon={Database} iconColor="text-zinc-400" items={masterItems} isActive={isActive} navigate={navigate} testId="nav-dropdown-masters" />
                {isAdmin && (
                  <NavDropdown label="Admin" icon={ShieldCheck} iconColor="text-red-400" items={adminItems} isActive={isActive} navigate={navigate} testId="nav-dropdown-admin" />
                )}
              </div>
            </div>

            {/* Right: notifications + user + logout */}
            <div className="flex items-center gap-3">
              <NotificationCenter />
              <span className="text-xs text-zinc-400">
                {user.name} <span className="text-zinc-600">({user.role})</span>
              </span>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white hover:bg-white/10 h-8 px-2"
                data-testid="logout-button"
              >
                <SignOut size={16} />
              </Button>
            </div>
          </div>
        </div>
      </nav>
      {children}
    </div>
  );
};
